import { useRef, useState } from "react";
import { ChevronsLeftRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { COMPARISONS } from "../data/content";

const CompareSlider = ({ pair }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const move = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="compare-slider"
      role="slider"
      aria-label="Before and after comparison"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
      }}
      onPointerDown={(e) => {
        ref.current.setPointerCapture(e.pointerId);
        setDragging(true);
        move(e.clientX);
      }}
      onPointerMove={(e) => dragging && move(e.clientX)}
      onPointerUp={() => setDragging(false)}
      className="relative aspect-[4/5] sm:aspect-[16/10] overflow-hidden rounded-2xl border border-[#E9DCC8] select-none cursor-ew-resize touch-pan-y outline-none focus-visible:border-[#B8893B]"
    >
      <img
        src={pair.before}
        alt={`Before makeup — ${pair.label}`}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover object-[50%_22%]"
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        aria-hidden="true"
      >
        <img
          src={pair.after}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover object-[50%_22%]"
        />
      </div>

      <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] bg-[#2A1F16]/60 backdrop-blur-md text-[#2A1F16] px-3.5 py-1.5 rounded-full">
        Before
      </span>
      <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.25em] bg-[#B8893B]/90 text-[#FBF6EE] px-3.5 py-1.5 rounded-full">
        After
      </span>

      <div
        className="absolute inset-y-0 w-px bg-[#B8893B]"
        style={{ left: `${pos}%` }}
        aria-hidden="true"
      >
        <span
          className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-[#C9A25F] text-[#2A1F16] flex items-center justify-center shadow-[0_10px_30px_-6px_rgba(184,137,59,0.7)] transition-transform duration-200 ${dragging ? "scale-110" : ""}`}
        >
          <ChevronsLeftRight size={20} />
        </span>
      </div>
    </div>
  );
};

export const Transformations = () => {
  const [active, setActive] = useState(0);
  const pair = COMPARISONS[active];

  return (
    <section id="transformations" data-testid="section-transformations" className="py-24 sm:py-32 bg-[#F4EBDD]">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center mb-12">
          <div className="flex justify-center">
            <Eyebrow index="04">Transformations</Eyebrow>
          </div>
          <h2 className="font-serif-ed font-normal text-3xl sm:text-4xl lg:text-5xl mt-6 text-[#2A1F16]">
            The art of <em className="text-[#96754D]">becoming</em>
          </h2>
          <p className="mt-4 text-[15px] text-[#6B5A49]">Drag the gold handle to compare.</p>
          <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#9C8871]">
            Sample comparisons — to be replaced with Mrinalee's real client transformations
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <AnimatePresence mode="wait">
            <motion.div
              key={pair.label}
              initial={{ opacity: 0, scale: 0.985 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <CompareSlider pair={pair} />
            </motion.div>
          </AnimatePresence>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {COMPARISONS.map((c, i) => (
              <button
                key={c.label}
                data-testid={`compare-pair-${i + 1}`}
                onClick={() => setActive(i)}
                className={`text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                  active === i
                    ? "bg-[#C9A25F] text-[#2A1F16] border-[#B8893B]"
                    : "border-[#E9DCC8] text-[#6B5A49] hover:border-[#B8893B] hover:text-[#2A1F16]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
